import React, {
	SetStateAction,
	useCallback,
	useEffect,
} from 'react';
import {
	Alert,
	Drawer,
	Form,
	Input,
} from 'antd';
import { Moment } from 'moment';
import { AttachmentTypeLabels, AttachmentTypes, Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import type { GetVoyageDetailsResponse } from '@api/features/voyages/getVoyageDetails';
import DatePicker from '@client/components/DatePicker';
import MultiCurrencyInput from '@client/components/MultiCurrencyInput';
import Button from '@client/components/Button';
import {
	createBrokerInvoice,
	updateBrokerInvoice,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import showErrorNotification from '@client/utils/showErrorNotification';
import useAttachments from '@client/utils/hooks/useAttachments';

const required = [{ required: true, message: 'Field is required' }];

type BrokerInvoiceForm = {
	id?: number;
	brokerId: number;
	brokerName: string;
	contract: string;
	invoiceId: string;
	voyageId: number;
	itemDescription: string;
	dateReceived: Moment;
	dueDate: Moment;
	amount: {
		value: number;
		currency: Values<typeof Currencies>;
		exchangeRate: number;
	};
};

type Props = {
	brokerId: number | undefined;
	brokerName: string;
	voyageId: number;
	contractIdentifier: string;
	currency: Values<typeof Currencies>;
	editValues: GetVoyageDetailsResponse['brokerInvoices'][number] | null;
	setEditValues: React.Dispatch<SetStateAction<GetVoyageDetailsResponse['brokerInvoices'][number] | null>>;
	voyageDetails: GetVoyageDetailsResponse;
	open: boolean;
	setOpen: React.Dispatch<SetStateAction<boolean>>;
	refreshDetails: () => void;
	confirmTitle: (row: GetVoyageDetailsResponse['brokerInvoices'][number], form?: boolean) => string | null;
};

const BrokerInvoiceDrawer = ({
	brokerId,
	brokerName,
	voyageId,
	contractIdentifier,
	currency,
	editValues,
	setEditValues,
	open,
	setOpen,
	refreshDetails,
	confirmTitle,
}: Props) => {
	const [form] = Form.useForm<BrokerInvoiceForm>();

	useEffect(() => {
		form.setFieldsValue({
			brokerName,
			contract: contractIdentifier,
		});
	}, [brokerName, contractIdentifier, form]);

	useEffect(() => {
		if (editValues != null) {
			form.setFieldsValue({
				id: editValues.id,
				amount: {
					value: editValues.amount,
					currency,
					exchangeRate: editValues.exchangeRate,
				},
				dueDate: editValues.dueDate,
				dateReceived: editValues.dateReceived,
				itemDescription: editValues.itemDescription,
			});
		}
	}, [currency, editValues, form]);

	const {
		uploaded,
		modal,
	} = useAttachments({
		attachments: editValues?.attachments ?? [],
		open,
		setOpen: () => setEditValues(null),
		attachmentTypeLabel: AttachmentTypeLabels[AttachmentTypes.BROKER_INVOICE],
		spacerWidth: '30%',
	});

	const handleSubmitInvoice = useCallback(async (vals: BrokerInvoiceForm) => {
		try {
			const values = {
				...vals,
				brokerId,
				voyageId,
				amount: vals.amount.value,
				currency: vals.amount.currency,
				exchangeRate: vals.amount.exchangeRate,
			};
			
			if (editValues != null) {
				await updateBrokerInvoice({
					...values,
					brokerInvoiceId: editValues.id,
					attachments: uploaded,
				});
			} else {
				await createBrokerInvoice({
					// @ts-ignore this is legal but even typeguards won't work 
					values,
					attachments: uploaded,
				});
			}

			setOpen(false);
			setEditValues(null);
			showSuccessNotification(`Broker invoice ${editValues == null ? 'created' : 'updated'}!`);
			refreshDetails();
		} catch (e) {
			showErrorNotification(`Could not ${editValues == null ? 'create' : 'update'} broker invoice`, e as Error);
		}
	}, [brokerId, editValues, refreshDetails, setEditValues, setOpen, uploaded, voyageId]);

	return (
		<>
			{modal}
			<Drawer
				title={editValues != null ? 'Edit Broker Invoice' : 'Create New Broker Invoice'}
				open={open}
				placement="left"
				width={'30%'}
				onClose={() => {
					setEditValues(null);
					form.resetFields();
					setOpen(false);
				}}
				mask={!open}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmitInvoice}
					preserve
				>
					<Form.Item
						name="brokerName"
						label="Broker Name"
					>
						<Input disabled />
					</Form.Item>
					<Form.Item
						name="contract"
						label="Contract Identifier"
					>
						<Input disabled />
					</Form.Item>
					<Form.Item
						name="itemDescription"
						label="Description"
						rules={required}
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="invoiceId"
						label="Invoice ID"
					>
						<Input />
					</Form.Item>
					<Form.Item
						name="dateReceived"
						label="Date Received"
						rules={required}
					>
						<DatePicker />
					</Form.Item>
					<Form.Item
						name="dueDate"
						label="Date Due"
					>
						<DatePicker />
					</Form.Item>
					<Form.Item
						name="amount"
						label="Amount"
						rules={required}
					>
						<MultiCurrencyInput
							baseCurrency={currency}
							placeholder="Amount"
						/>
					</Form.Item>
					{editValues != null && confirmTitle(editValues) != null && (
						<>
							<Alert
								message={confirmTitle(editValues, true)}
								type="warning"
							/>
							<br />
						</>
					)}
					<Button
						htmlType="submit"
						type="primary"
					>
						Save Invoice
					</Button>
				</Form>
			</Drawer>
		</>
	);
};

export default BrokerInvoiceDrawer;
